import { useEffect, useMemo, useRef, useState } from 'react'
import { TransitionLoader } from '@opallabs/kuiper'
import { useOnce } from '@opallabs/lambda'
import { APPROVAL_GLYPH } from '@opallabs/lapidary'
import type { WithTheme } from '@opallabs/lapidary'
import { ApprovalItem } from '@opallabs/satellite'
import { AssignmentStatus } from '@opallabs/satellite/src/AssignmentHelpers/types'
import { NormalizedApproval } from '@opallabs/wormhole/types'
import { EmptyState } from '../../Shared/EmptyState'
import * as sharedStyles from '../../Shared/styles'
import { Runnable } from '../../types'

export type CompletedPanelProps = Readonly<
  WithTheme & {
    completedApprovals: NormalizedApproval[]
    createCheckpoint: Runnable
    getCompletedApprovals: Runnable
    isLoading: boolean
    setApprovalPreviewCard: (approval: NormalizedApproval | null) => void
  }
>

export const CompletedPanel: React.FC<CompletedPanelProps> = ({
  completedApprovals,
  createCheckpoint,
  getCompletedApprovals,
  isLoading,
  setApprovalPreviewCard,
  theme = 'light',
}) => {
  const checkpointCreated = useRef(false)

  const [activeApproval, setActiveApproval] = useState<string>(
    completedApprovals.length ? completedApprovals[0].id : ''
  )

  const isEmpty = useMemo(
    () => completedApprovals.length === 0,
    [completedApprovals]
  )

  useEffect(() => {
    if (!checkpointCreated.current && !isLoading) {
      checkpointCreated.current = true
      createCheckpoint()
    }
  }, [checkpointCreated, createCheckpoint, isLoading])

  useOnce(getCompletedApprovals)

  if (!isLoading && isEmpty) {
    return (
      <EmptyState
        icon={APPROVAL_GLYPH}
        text="You don’t have any completed approvals right now"
        theme={theme}
      />
    )
  }

  return (
    <TransitionLoader
      loaded={!isLoading}
      minHeight={600} // this is an arbitrary size that looks good
      theme="transparent"
    >
      <div>
        <h3 css={sharedStyles.title({ theme })}>My Completed Approvals</h3>
        {completedApprovals.map((approval) => (
          <ApprovalItem
            {...approval}
            key={`approval-completed-${approval.id}`}
            assignmentStatus={
              activeApproval === approval.id
                ? AssignmentStatus.Active
                : approval.assignmentStatus
            }
            onItemClick={(): void => {
              setActiveApproval(approval.id)
              setApprovalPreviewCard(approval)
            }}
          />
        ))}
      </div>
    </TransitionLoader>
  )
}
